import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import colors from "../ColorConfig/colorconfig";
import GetInTouchForm from "../GetinTouchform/GetinTouch";
import Readytostart from "../Products/readytostart";
import ServiceInfo from "./serviceinfo";
import SEO from "../../AASeo/Seo"; // Add SEO import

const organization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "IDLBAY Private Limited",
  alternateName: "IDLBAY",
  url: "https://idlbay.com",
  logo: "https://idlbay.com/idlbaylogo.png",
  sameAs: [
    "https://www.linkedin.com/company/idlbay/",
    "https://x.com/idlbayPvtLtd",
    "https://www.instagram.com/idlbay.official?igsh=dnIxMm50cmdxNDNy",
    "https://www.facebook.com/profile.php?id=61582746578911",
    "https://www.youtube.com/channel/UCDSb9__-mSLqeyuZYelakcw",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-9840841840",
    contactType: "Customer Support",
    areaServed: "IN",
    availableLanguage: ["English", "Tamil"],
  },
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "Office No.1, First Floor, Door No.1, B Narasimhan 3rd Street",
    addressLocality: "T-Nagar, Chennai",
    addressRegion: "Tamil Nadu",
    postalCode: "600017",
    addressCountry: "IN",
  },
};

const ServicesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  // State for dynamic services data
  const [servicesData, setServicesData] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const selectedServiceId = location.state?.selectedServiceId || null;

  const [activeService, setActiveService] = useState(selectedServiceId);
  const [expandedServices, setExpandedServices] = useState({});
  const [hoveredCard, setHoveredCard] = useState(null);
  const serviceRefs = useRef({});

  const [showGetInTouch, setShowGetInTouch] = useState(false);
  const sectionRef = useRef(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Function to fetch services from API
  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8923_Idlbay_Services_ServicesList"
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform API data to match the component structure
      const transformedData = data.map((service) => ({
        id: parseInt(service.id),
        title: service.title,
        description: service.description,
        fullDescription: service.fullDescription,
        category: service.category,
        features: JSON.parse(service.features || "[]"),
        benefits: JSON.parse(service.benefits || "[]"),
        image: service.media
          ? `https://api.dremerz.net/uploads/${service.media}`
          : "",
        datetime: service.datetime,
      }));

      setServicesData(transformedData);
      setFilteredServices(transformedData); // Initialize filtered services with all services
      setError(null);
    } catch (err) {
      console.error("Error fetching services:", err);
      setError(err.message);
      // Fallback to empty array if API fails
      setServicesData([]);
      setFilteredServices([]);
    } finally {
      setLoading(false);
    }
  };

  // Fixed search functionality - only searches title and category
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredServices(servicesData);
    } else {
      const searchTermLower = searchQuery.toLowerCase().trim();

      const filtered = servicesData.filter((service) => {
        // Only search in title and category
        const titleMatch =
          service.title?.toLowerCase().includes(searchTermLower) || false;
        const categoryMatch =
          service.category?.toLowerCase().includes(searchTermLower) || false;

        return titleMatch || categoryMatch;
      });

      setFilteredServices(filtered);
    }
  }, [searchQuery, servicesData]);

  // Handle search from ServiceInfo component
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // Fetch services on component mount
  useEffect(() => {
    fetchServices();
  }, []);

  // Function to show GetInTouch form
  const handleContactClick = (e) => {
    e.preventDefault();
    setShowGetInTouch(true);
    document.body.style.overflow = "hidden";
  };

  // Function to close GetInTouch form
  const closeGetInTouch = () => {
    setShowGetInTouch(false);
    document.body.style.overflow = "auto";
  };

  // Function to navigate to service details
  const handleServiceClick = (service) => {
    navigate(`/servicedetails/${service.id}`, {
      state: { serviceData: service },
    });
  };

  // Add custom CSS styles
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .service-card-container {
        perspective: 1000px;
        transition: all 0.6s cubic-bezier(0.23, 1, 0.320, 1);
        cursor: pointer;
      }

      .service-card-container:hover {
        transform: translateY(-15px) rotateX(5deg);
        z-index: 10;
      }

      .service-card {
        position: relative;
        overflow: hidden;
        border-radius: 20px;
        transition: all 0.6s cubic-bezier(0.23, 1, 0.320, 1);
        transform-style: preserve-3d;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      }

      .service-card:hover {
        box-shadow: 0 30px 60px rgba(229, 118, 39, 0.4);
      }

      .service-card-image {
        transition: all 0.8s cubic-bezier(0.23, 1, 0.320, 1);
        height: ${isMobile ? "200px" : "280px"};
        object-fit: cover;
        width: 100%;
      }

      .service-card:hover .service-card-image {
        transform: scale(1.1);
        filter: brightness(0.7);
      }

      .service-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
          135deg,
          rgba(229, 118, 39, 0.9) 0%,
          rgba(0, 0, 0, 0.8) 100%
        );
        opacity: 0;
        transition: all 0.6s cubic-bezier(0.23, 1, 0.320, 1);
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        padding: ${isMobile ? "1rem" : "2rem"};
        text-align: left;
      }

      .service-card:hover .service-overlay {
        opacity: 1;
      }

      .service-content {
        transform: translateY(60px);
        transition: all 0.6s cubic-bezier(0.23, 1, 0.320, 1) 0.1s;
        opacity: 0;
        width: 100%;
        text-align: left;
      }

      .service-card:hover .service-content {
        transform: translateY(0);
        opacity: 1;
      }

      .service-title {
        font-size: ${isMobile ? "1.2rem" : "1.5rem"};
        font-weight: 700;
        color: ${colors.white};
        margin-bottom: 0.8rem;
        text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
        text-align: left;
        width: 100%;
      }

      .service-description {
        color: ${colors.whiteTransparent};
        line-height: 1.5;
        margin-bottom: 1.3rem;
        font-size: ${isMobile ? "0.85rem" : "0.95rem"};
        text-align: left;
        width: 100%;
        display: -webkit-box;
        -webkit-line-clamp: 5;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        // padding: 5px;
        max-height: ${isMobile ? "6.5em" : "7.5em"};
      }
      
      .servicecard-title {
        color: ${colors.white};
        padding: 10px;
        font-size: ${isMobile ? "1rem" : "1.2rem"};
        text-align: left;
      }

      .service-features {
        margin-bottom: 1.2rem;
      }

      .service-feature-item {
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
        padding: 0.4rem 0.8rem;
        border-radius: 20px;
        font-size: ${isMobile ? "0.7rem" : "0.8rem"};
        color: ${colors.white};
        margin: 0.2rem 0.2rem 0.2rem 0;
        display: inline-block;
        border: 1px solid rgba(255, 255, 255, 0.2);
      }

      .service-btn {
        background: rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(10px);
        border: 2px solid ${colors.white};
        color: ${colors.white};
        padding: ${isMobile ? "0.6rem 1.5rem" : "0.8rem 2rem"};
        border-radius: 30px;
        font-weight: 600;
        transition: all 0.3s ease;
        text-transform: uppercase;
        letter-spacing: 1px;
        font-size: ${isMobile ? "0.8rem" : "0.9rem"};
      }

      .service-btn:hover {
        background: ${colors.white};
        color: var(--color-primary);
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(255, 255, 255, 0.3);
      }

      .category-badge {
        position: absolute;
        top: 0.8rem;
        right: 0.8rem;
        background: linear-gradient(135deg, var(--color-primary) 0%, ${
          colors.primaryHover
        } 100%);
        color: ${colors.white};
        padding: 0.4rem 0.8rem;
        border-radius: 15px;
        font-size: ${isMobile ? "0.7rem" : "0.8rem"};
        font-weight: 600;
        z-index: 2;
        backdrop-filter: blur(10px);
        box-shadow: 0 4px 15px rgba(229, 118, 39, 0.3);
      }

      .service-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(${
          isMobile ? "300px" : "350px"
        }, 1fr));
        gap: ${isMobile ? "1.5rem" : "2rem"};
        padding: ${isMobile ? "1rem 0" : "2rem 0"};
      }

      .loading-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
        flex-direction: column;
        color: ${colors.white};
      }

      .loading-spinner {
        width: 40px;
        height: 40px;
        border: 4px solid rgba(229, 118, 39, 0.3);
        border-top: 4px solid var(--color-primary);
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin-bottom: 1rem;
      }

      .error-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
        flex-direction: column;
        color: ${colors.white};
        text-align: center;
      }

      .error-message {
        background: rgba(220, 38, 127, 0.1);
        border: 1px solid rgba(220, 38, 127, 0.3);
        padding: 1rem 2rem;
        border-radius: 10px;
        margin-bottom: 1rem;
      }

      .retry-button {
        background: var(--color-primary);
        color: ${colors.white};
        border: none;
        padding: 0.8rem 1.5rem;
        border-radius: 25px;
        cursor: pointer;
        font-weight: 600;
        transition: all 0.3s ease;
      }

      .retry-button:hover {
        background: ${colors.primaryHover};
        transform: translateY(-2px);
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      @media (max-width: 768px) {
        .service-grid {
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        
        .service-card-container:hover {
          transform: translateY(-10px);
        }
      }

      /* Search results info */
      .search-results-info {
        color: ${colors.white};
        margin-bottom: 1rem;
        padding: 1rem;
        background: rgba(229, 118, 39, 0.1);
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 1rem;
        font-size: ${isMobile ? "0.9rem" : "1rem"};
      }
      
      .clear-search {
        background: var(--color-primary);
        color: ${colors.white};
        border: none;
        padding: 0.5rem 1rem;
        border-radius: 5px;
        cursor: pointer;
        transition: all 0.3s ease;
      }

      .clear-search:hover {
        background: ${colors.primaryHover};
      }

      .no-results {
        text-align: center;
        padding: 3rem;
        color: ${colors.white};
        grid-column: 1 / -1;
      }

      .no-results h3 {
        margin-bottom: 1rem;
        color: var(--color-primary);
      }

      .no-results p {
        margin-bottom: 1.5rem;
        opacity: 0.8;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, [isMobile]);

  // Toggle service details
  const toggleServiceDetails = (serviceId, service, e) => {
    e.stopPropagation();
    if (expandedServices[serviceId]) {
      setExpandedServices((prev) => ({
        ...prev,
        [serviceId]: false,
      }));
    } else {
      // Navigate to details page instead of expanding
      handleServiceClick(service);
    }
  };

  // Scroll to specific service when component mounts or selectedServiceId changes
  useEffect(() => {
    window.scrollTo(0, 0);

    if (
      selectedServiceId &&
      selectedServiceId !== "all" &&
      servicesData.length > 0
    ) {
      setActiveService(selectedServiceId);

      setTimeout(() => {
        const serviceElement = serviceRefs.current[selectedServiceId];
        if (serviceElement) {
          serviceElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });

          // Add highlight effect
          serviceElement.style.transition = "all 0.3s ease";
          serviceElement.style.transform = "scale(1.02)";
          serviceElement.style.boxShadow = `0 0 30px var(--color-primary)50`;

          setTimeout(() => {
            serviceElement.style.transform = "scale(1)";
            serviceElement.style.boxShadow = "none";
          }, 2000);
        }
      }, 100);
    }
  }, [selectedServiceId, servicesData]);

  const ServiceCard = ({ service, index }) => {
    const isEven = index % 2 === 0;

    return (
      <div
        className={`service-card-container ${index % 4 === 0 ? "" : ""} ${
          index % 3 === 0 ? "" : ""
        }`}
        ref={(el) => (serviceRefs.current[service.id] = el)}
        id={`service-${service.id}`}
        style={{
          animationDelay: `${index * 0.2}s`,
        }}
        onClick={() => handleServiceClick(service)}
      >
        <div className="service-card">
          <img
            src={service.image}
            alt={service.title}
            className="service-card-image"
            onError={(e) => {
              e.target.src =
                "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80";
            }}
          />

          <div className="category-badge">{service.category}</div>

          <div className="service-overlay">
            <div className="service-content">
              <h1 className="service-title">{service.title}</h1>
              <h2 className="service-description">{service.description}</h2>
            </div>
          </div>
          <div className="servicecard-title">{service.title}</div>
        </div>
      </div>
    );
  };

  // Loading component
  const LoadingComponent = () => (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <h3>Loading Services...</h3>
      <p>Please wait while we fetch the latest services.</p>
    </div>
  );

  // Error component
  const ErrorComponent = () => (
    <div className="error-container">
      <div className="error-message">
        <h3>Error Loading Services</h3>
        <p>Unable to fetch services: {error}</p>
      </div>
      <button className="retry-button" onClick={fetchServices}>
        Retry
      </button>
    </div>
  );

  return (
    <div
      style={{ backgroundColor: colors.bodyBlack, minHeight: "100vh" }}
      className="py-1"
    >
      <SEO
        title="Our Services | IDLBAY - IT Solutions & Digital Services"
        description="Explore the wide range of IT services offered by IDLBAY, including software development, web and mobile app solutions, and digital marketing."
        keywords="IDLBAY services, IT solutions, software development, web development, mobile apps, digital marketing, Chennai"
        url="https://idlbay.com/services"
        organization={organization}
      />
      <div className="container">
        <ServiceInfo onSearch={handleSearch} searchQuery={searchQuery} />

        {/* Loading State */}
        {loading && <LoadingComponent />}

        {/* Error State */}
        {error && !loading && <ErrorComponent />}

        {/* Services Grid */}
        {!loading && !error && filteredServices.length > 0 && (
          <div className="service-grid">
            {filteredServices.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        )}

        {/* No results found */}
        {!loading && !error && searchQuery && filteredServices.length === 0 && (
          <div className="no-results">
            <h3>No services found for "{searchQuery}"</h3>
            <p>Try searching with different keywords or browse all services</p>
            <button className="retry-button" onClick={() => setSearchQuery("")}>
              View All Services
            </button>
          </div>
        )}

        {/* Empty State (no services at all) */}
        {!loading && !error && servicesData.length === 0 && !searchQuery && (
          <div className="error-container">
            <h3 style={{ color: colors.white }}>No Services Available</h3>
            <p style={{ color: colors.whiteTransparent }}>
              No services found. Please check back later.
            </p>
          </div>
        )}
      </div>

      {showGetInTouch && (
        <div className="modal-overlay" onClick={closeGetInTouch}>
          <div
            className="modal-content-custom"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-button" onClick={closeGetInTouch}>
              &times;
            </button>
            <GetInTouchForm isModal={true} />
          </div>
        </div>
      )}
      <Readytostart />
    </div>
  );
};

export default ServicesPage;
